import {
  Component,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { TranslocoModule } from '@jsverse/transloco';

import { AuthService } from '../../services/auth.service';
import { GlobalSearchService } from '../../services/global-search.service';
import { NotificationService, Notification } from '../../services/vendorService/notifications.service';
import { InboxService } from '../../services/inbox.service';
import { RealtimeService } from '../../services/realtime.service';
import { ToastService } from '../../ui/toast/toast.service';
import { BrandLogoComponent } from '../brand-logo/brand-logo.component';
import { ThemeToggleComponent } from '../../ui/theme-toggle/theme-toggle.component';
import { LanguageSwitcherComponent } from '../../i18n/language-switcher.component';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslocoModule,
    BrandLogoComponent,
    ThemeToggleComponent,
    LanguageSwitcherComponent,
  ],
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.css'],
})
export class NavbarComponent implements OnInit, OnDestroy {
  @Output() toggleSidebarEvent = new EventEmitter<void>();

  readonly notifications = signal<Notification[]>([]);
  readonly unreadNotifications = signal<number>(0);
  readonly inboxUnread = signal<number>(0);

  readonly notificationsOpen = signal<boolean>(false);
  readonly profileOpen = signal<boolean>(false);
  readonly mobileMenuOpen = signal<boolean>(false);

  readonly displayName = signal<string>('');
  readonly displayEmail = signal<string>('');
  readonly displayRole = signal<string>('');
  readonly isMac = signal(
    typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform),
  );

  private notificationSub?: Subscription;
  private allNotificationsSub?: Subscription;
  private realtimeNotifSub?: Subscription;
  private inboxTimer?: any;

  constructor(
    private auth: AuthService,
    private notif: NotificationService,
    private inbox: InboxService,
    private router: Router,
    private realtime: RealtimeService,
    private toast: ToastService,
    private globalSearch: GlobalSearchService,
  ) {}

  ngOnInit(): void {
    const userId = this.auth.getUserId();

    // Profil affiché
    try {
      const fullname = (this.auth as any).getFullnameFromToken?.() || '';
      const email = (this.auth as any).getEmailFromToken?.() || '';
      const roles: string[] = this.auth.getRolesFromToken?.() || [];
      this.displayName.set(fullname || email || 'Vendeur');
      this.displayEmail.set(email);
      this.displayRole.set((roles && roles.length ? roles[0] : '') || 'utilisateur');
    } catch {}

    if (userId !== null) {
      this.notif.initializeNotifications(userId);
      this.allNotificationsSub = this.notif.allNotifications$.subscribe({
        next: (list) => {
          this.notifications.set(list || []);
          this.unreadNotifications.set((list || []).filter((n) => !n.is_read).length);
        },
      });
      this.notificationSub = this.notif.notifications$.subscribe({
        next: (newOne) => {
          this.notifications.update((list) => [{ ...newOne, is_read: false } as Notification, ...list]);
          this.unreadNotifications.update((n) => n + 1);
        },
      });
    }

    // Notifications temps réel (SSE) : toast immédiat + alimentation du badge/
    // liste via le NotificationService (réémet sur notifications$, géré ci-dessus).
    // Best-effort : si le live est indisponible, le polling existant prend le relais.
    this.realtimeNotifSub = this.realtime.notifications$.subscribe({
      next: (ev) => {
        try {
          const title = (ev?.title || '').trim();
          const body = (ev?.body || '').trim();
          this.toast.info(body || title || 'Nouvelle notification', title || undefined);
          this.notif.ingestRealtimeNotification(title, body, ev?.ts);
        } catch {
          /* live best-effort : ne casse jamais la navbar */
        }
      },
    });

    this.refreshInboxBadge();
    this.inboxTimer = setInterval(() => this.refreshInboxBadge(), 60_000);
  }

  ngOnDestroy(): void {
    this.notificationSub?.unsubscribe();
    this.allNotificationsSub?.unsubscribe();
    this.realtimeNotifSub?.unsubscribe();
    this.notif.stopNotifications();
    if (this.inboxTimer) clearInterval(this.inboxTimer);
  }

  toggleSidebar(): void {
    this.toggleSidebarEvent.emit();
    this.mobileMenuOpen.update((v) => !v);
  }

  openGlobalSearch(): void {
    this.notificationsOpen.set(false);
    this.profileOpen.set(false);
    this.globalSearch.show();
  }

  toggleNotifications(): void {
    this.notificationsOpen.update((v) => !v);
    this.profileOpen.set(false);
  }

  toggleProfile(): void {
    this.profileOpen.update((v) => !v);
    this.notificationsOpen.set(false);
  }

  markNotificationRead(id: string): void {
    this.notif.markAsRead(id);
    this.notifications.update((list) =>
      list.map((n) => (n.public_id === id ? { ...n, is_read: true } : n)),
    );
    this.unreadNotifications.set(this.notifications().filter((n) => !n.is_read).length);
  }

  goToInbox(): void {
    this.router.navigateByUrl('/inbox');
  }

  goToProfile(): void {
    this.router.navigateByUrl('/profile');
    this.profileOpen.set(false);
  }

  goToSettings(): void {
    this.router.navigateByUrl('/settings');
    this.profileOpen.set(false);
  }

  /**
   * Vide entièrement le cache de la PWA.
   *
   * ``force-reload.html`` est un fichier STATIQUE (servi à la racine), pas une
   * route Angular : on doit donc faire une vraie navigation navigateur
   * (``window.location``) et NON ``routerLink`` (que le Router SPA
   * intercepterait → 404). ``?auto=true`` lance la purge dès l'arrivée
   * (désinscription SW + Cache Storage + IndexedDB + local/session storage),
   * puis la page redirige vers ``/`` avec un cache-bust.
   */
  clearCache(): void {
    this.profileOpen.set(false);
    if (typeof window !== 'undefined') {
      window.location.href = '/force-reload.html?auto=true';
    }
  }

  logout(): void {
    // Coupe le flux temps réel AVANT de quitter la session (aucune connexion
    // zombie ni reconnexion en boucle après déconnexion).
    this.realtime.disconnect();
    this.auth.logout().subscribe({
      next: () => this.router.navigate(['/login']),
      error: () => this.router.navigate(['/login']),
    });
  }

  getInitials(): string {
    const name = this.displayName() || this.displayEmail();
    if (!name) return 'VD';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  }

  notificationTone(n: Notification): 'primary' | 'accent' | 'warning' | 'neutral' {
    const m = (n.message || '').toLowerCase();
    if (m.includes('commande') || m.includes('order')) return 'primary';
    if (m.includes('livraison') || m.includes('delivery')) return 'accent';
    if (m.includes('alerte') || m.includes('attention')) return 'warning';
    return 'neutral';
  }

  formatTime(iso?: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const diffH = (Date.now() - d.getTime()) / 36e5;
    if (diffH < 1) return `il y a ${Math.max(1, Math.floor(diffH * 60))} min`;
    if (diffH < 24) return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  }

  private refreshInboxBadge(): void {
    this.inbox.stats().subscribe({
      next: (s) => this.inboxUnread.set(s?.unread || 0),
      error: () => {},
    });
  }

  @HostListener('document:click', ['$event'])
  onDocClick(ev: Event) {
    const target = ev.target as HTMLElement;
    if (
      !target.closest('.nav-notif-wrap') &&
      !target.closest('.nav-profile-wrap')
    ) {
      this.notificationsOpen.set(false);
      this.profileOpen.set(false);
    }
  }

  trackNotif(_: number, n: Notification) { return n.public_id; }
}
